/**
 * React Query Error Handling for Club Queries
 * 
 * Provides utilities for handling club-related query errors with
 * user-friendly messages and recovery suggestions.
 * 
 * This hook reduces component complexity by centralizing error handling logic.
 */

import { 
  isNetworkError, 
  isServerError, 
  isConnectivityError,
  isValidationError,
  getErrorTitle,
  getDetailedErrorMessage,
  getUserErrorMessage
} from '~/lib/apiClient/errors';

/**
 * Error state with user-friendly messages
 */
export interface ClubQueryError {
  /** Original error object */
  error: Error;
  /** Whether error is network-related (can retry) */
  isNetwork: boolean;
  /** Whether error is server-related (5xx) */
  isServer: boolean;
  /** Whether error is connectivity-related (network or server) */
  isConnectivity: boolean;
  /** Whether error is a validation/configuration error */
  isValidation: boolean;
  /** User-friendly title */
  title: string;
  /** User-friendly message */
  message: string;
  /** Detailed message with recovery suggestions */
  detailedMessage: string;
  /** Whether user should retry */
  canRetry: boolean;
  /** Retry action (if available) */
  onRetry?: () => void;
}

/**
 * Hook to process React Query errors for club data
 * 
 * Provides pre-computed error states and messages so components
 * don't need to parse errors themselves.
 * 
 * **Enhanced with Retry Actions**: Now includes an `onRetry` function
 * that components can call to retry the query.
 * 
 * @param error - Error from React Query
 * @param refetch - Refetch function from React Query (optional)
 * @returns Processed error information or null if no error
 * 
 * @example
 * ```tsx
 * function ClubInfo() {
 *   const query = useClubQuery();
 *   const error = useClubQueryError(query.error, query.refetch);
 *   
 *   if (error) {
 *     return (
 *       <BookingError
 *         title={error.title}
 *         message={error.detailedMessage}
 *         error={error.error}
 *         context={{
 *           category: error.isConnectivity ? 'api_error' : 'data_validation',
 *           type: error.isNetwork ? 'network_error' : 'server_error',
 *           severity: 'error'
 *         }}
 *         action={error.onRetry ? {
 *           label: 'Retry',
 *           onClick: error.onRetry
 *         } : undefined}
 *       />
 *     );
 *   }
 *   
 *   // ... rest of component
 * }
 * ```
 */
export function useClubQueryError(
  error: Error | null, 
  refetch?: () => void
): ClubQueryError | null {
  if (!error) return null;
  
  const isNetwork = isNetworkError(error);
  const isServer = isServerError(error);
  const isConnectivity = isConnectivityError(error);
  const isValidation = isValidationError(error);
  
  return {
    error,
    isNetwork,
    isServer,
    isConnectivity,
    isValidation,
    title: getErrorTitle(error),
    message: getUserErrorMessage(error),
    detailedMessage: getDetailedErrorMessage(error),
    canRetry: isConnectivity, // Only retry connectivity errors
    onRetry: isConnectivity && refetch ? refetch : undefined,
  };
}

/**
 * Get error category for BookingError component
 * 
 * @param error - Processed error from useClubQueryError
 * @returns Error category for monitoring
 */
export function getErrorCategory(error: ClubQueryError): 'api_error' | 'data_validation' {
  return error.isConnectivity ? 'api_error' : 'data_validation';
}

/**
 * Get error type for monitoring
 * 
 * @param error - Processed error from useClubQueryError
 * @returns Specific error type
 */
export function getErrorType(error: ClubQueryError): string {
  if (error.isNetwork) return 'network_error';
  if (error.isServer) return 'server_error';
  return 'fetch_error';
}
